// Реестры расширений редактора. Каждая функция (src/features/<имя>/index.ts) регистрирует здесь свои
// команды, кнопки ленты, пункты контекстного меню, обработчики вставки, TipTap-расширения, отображения узлов,
// боковые панели, правила живой проверки, диалоги и элементы строки состояния - и ничего не правит в чужих
// модулях. Порядок - поле order (меньше - раньше). Подробно - web/ARCHITECTURE.md, раздел «Реестры».
import type { AnyExtension, Editor, NodeViewProps } from '@tiptap/react';
import type { Slice } from '@tiptap/pm/model';
import type { ComponentType, ReactNode } from 'react';
import type { Api } from './api';
import type { AppStore } from './store';
import type { DocIndex } from './docIndex';

// ------------------------------------------------------------------------------------------------ контекст
/** Контекст, передаваемый командам, обработчикам и правилам. */
export interface Ctx {
  editor: Editor | null;
  store: AppStore;
  api: Api;
  /** открыть зарегистрированный диалог; промис - результат (undefined при отмене) */
  openDialog: <T = unknown>(id: string, props?: Record<string, unknown>) => Promise<T | undefined>;
  toast: (text: string, kind?: 'info' | 'ok' | 'warn' | 'error', ms?: number) => void;
  run: (commandId: string, arg?: unknown) => unknown;
}

// ------------------------------------------------------------------------------------------------ базовый реестр
type Listener = () => void;

export class Registry<T extends { id: string; order?: number }> {
  private items = new Map<string, T>();
  private listeners = new Set<Listener>();
  private cache: T[] | null = null;
  /** id, добавленные повторно (заменившие прежний элемент) */
  readonly replaced: string[] = [];
  constructor(public readonly name: string) {}
  /** Добавить (или заменить по id). Возвращает функцию удаления. */
  add(item: T): () => void {
    // замену по id запоминаем - интегратор/отладка видят, кто кого перекрыл (window.sto.registry.<x>.replaced)
    if (this.items.has(item.id)) this.replaced.push(item.id);
    this.items.set(item.id, item);
    this.changed();
    return () => this.remove(item.id);
  }
  remove(id: string) { if (this.items.delete(id)) this.changed(); }
  get(id: string): T | undefined { return this.items.get(id); }
  has(id: string) { return this.items.has(id); }
  /** Все элементы по возрастанию order (затем по порядку добавления). Ссылка стабильна до изменения. */
  list(): T[] {
    if (!this.cache) {
      const arr = [...this.items.values()];
      this.cache = arr.map((x, i) => [x, i] as const).sort((a, b) => ((a[0].order ?? 100) - (b[0].order ?? 100)) || (a[1] - b[1])).map((x) => x[0]);
    }
    return this.cache;
  }
  subscribe(fn: Listener) { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; }
  private changed() { this.cache = null; this.listeners.forEach((f) => f()); }
}

// ------------------------------------------------------------------------------------------------ команды
export interface CommandSpec {
  id: string;                       // 'область.действие', напр. 'insert.figure', 'format.bold'
  title: string;                    // подпись/подсказка
  description?: string;
  icon?: string;                    // имя значка из ui/icons (lucide) или своя строка SVG
  /** сочетание клавиш в формате TipTap: 'Mod-b', 'Mod-Shift-e', 'Alt-F12' (Mod = Ctrl) */
  shortcut?: string;
  run: (ctx: Ctx, arg?: unknown) => unknown;
  isEnabled?: (ctx: Ctx) => boolean;
  isActive?: (ctx: Ctx) => boolean;
  order?: number;
}

// ------------------------------------------------------------------------------------------------ лента
export interface RibbonTab { id: string; title: string; order?: number; contextual?: (ctx: Ctx) => boolean }
export interface RibbonGroup { id: string; tab: string; title: string; order?: number }
export interface MenuItemSpec {
  id?: string;
  label?: string; icon?: string; command?: string; arg?: unknown; shortcut?: string;
  onClick?: (ctx: Ctx) => void;
  separator?: boolean; disabled?: boolean; checked?: boolean;
  children?: MenuItemSpec[];
  hint?: string;
}
export interface RibbonItem {
  id: string;
  group: string;
  order?: number;
  /** button - кнопка; large - большая кнопка; toggle - залипающая (isActive); menu - выпадающее меню;
   *  split - кнопка + меню; gallery - галерея стилей; custom - своя разметка (render) */
  type: 'button' | 'large' | 'toggle' | 'menu' | 'split' | 'gallery' | 'custom';
  command?: string; arg?: unknown;
  label?: string; icon?: string; hint?: string;
  items?: MenuItemSpec[] | ((ctx: Ctx) => MenuItemSpec[]);
  render?: (ctx: Ctx) => ReactNode;
}

// ------------------------------------------------------------------------------------------------ контекстное меню
export interface ContextTarget {
  /** позиция в документе и узлы под курсором (от внешнего к внутреннему) */
  pos: number | null;
  nodes: { type: string; attrs: Record<string, any>; pos: number }[];
  event: MouseEvent;
  inTable: boolean;
  selectionEmpty: boolean;
}
export interface ContextMenuProvider {
  id: string; order?: number;
  items: (ctx: Ctx, target: ContextTarget) => MenuItemSpec[] | null;
}

// ------------------------------------------------------------------------------------------------ вставка
export interface PasteData {
  kind: 'paste' | 'drop';
  event: ClipboardEvent | DragEvent;
  files: File[];
  html: string;
  text: string;
  rtf: string;
  types: string[];
  /** срез ProseMirror, который вставился бы по умолчанию */
  slice: Slice | null;
  /** позиция сброса при drop */
  pos: number | null;
  /** внутреннее перетаскивание внутри редактора */
  moved: boolean;
}
export interface PasteHandler {
  id: string;
  /** больше - раньше. Ориентиры: 100 файлы-изображения, 80 HTML из Word/Excel, 60 MathML/LaTeX, 40 текст */
  priority: number;
  match: (data: PasteData, ctx: Ctx) => boolean;
  /** true (или промис) - вставка обработана, стандартная вставка ProseMirror не выполняется */
  handle: (data: PasteData, ctx: Ctx) => boolean | Promise<unknown>;
}
/** Преобразование HTML из буфера перед разбором ProseMirror (очистка Word/Excel и т. п.). */
export interface HtmlTransform { id: string; order?: number; transform: (html: string, ctx: Ctx) => string }

// ------------------------------------------------------------------------------------------------ редактор
export interface ExtensionSpec { id: string; order?: number; create: () => AnyExtension | AnyExtension[] }
/** Отображение узла (React node view). Заменяет стандартное отображение узла схемы из core/schema. */
export interface NodeViewSpec {
  id: string;                 // = имя типа узла ('figure', 'formula', …)
  component: ComponentType<NodeViewProps>;
  /** as, className, contentDOMElementTag… - параметры ReactNodeViewRenderer */
  options?: Record<string, unknown>;
}

// ------------------------------------------------------------------------------------------------ панели, проверка, диалоги
export interface PanelSpec {
  id: string; title: string; icon?: string; side: 'left' | 'right'; order?: number;
  component: ComponentType<{ ctx: Ctx }>;
  /** число на значке панели (например, число замечаний) */
  badge?: (ctx: Ctx) => number | string | null;
  /** цвет значка: error - красный (ошибки), иначе нейтральный */
  badgeTone?: 'error' | 'neutral';
}
export interface LintIssue {
  sev: 'E' | 'W' | 'I';
  msg: string;
  node: string | null;      // id узла
  rule?: string;            // пункт СТО
  code?: string;            // код правила (как у sto_check, если правило общее) - для «исправить все однотипные»
  fix?: { label: string; run: (ctx: Ctx) => void };
}
export interface LintRule { id: string; title: string; order?: number; run: (index: DocIndex, ctx: Ctx) => LintIssue[] }
export interface DialogProps<T = unknown> { ctx: Ctx; props: Record<string, any>; close: (result?: T) => void }
export interface DialogSpec { id: string; component: ComponentType<DialogProps<any>> }
export interface StatusItem { id: string; side: 'left' | 'right'; order?: number; component: ComponentType<{ ctx: Ctx }> }
/** Блок на стартовом экране (список проектов): примеры, импорт, справка… */
export interface StartSection { id: string; order?: number; component: ComponentType<{ ctx: Ctx }> }
/** Лист перед основным текстом (титульный лист, задание, реферат - по метаданным): отдельный лист A4 над редактором. */
export interface SheetSpec { id: string; order?: number; visible?: (ctx: Ctx) => boolean; component: ComponentType<{ ctx: Ctx }> }

// ------------------------------------------------------------------------------------------------ события
type EventName = 'editor:created' | 'project:opened' | 'project:closed' | 'doc:saved' | 'doc:save-refused' | 'check:done' | 'meta:changed';
class Events {
  private m = new Map<string, Set<(p: any) => void>>();
  on(name: EventName, fn: (payload: any) => void) {
    if (!this.m.has(name)) this.m.set(name, new Set());
    this.m.get(name)!.add(fn);
    return () => this.m.get(name)!.delete(fn);
  }
  emit(name: EventName, payload?: unknown) { this.m.get(name)?.forEach((f) => { try { f(payload); } catch (e) { console.error(e); } }); }
}

// ------------------------------------------------------------------------------------------------ единый объект
export const registry = {
  commands: new Registry<CommandSpec>('commands'),
  ribbonTabs: new Registry<RibbonTab>('ribbonTabs'),
  ribbonGroups: new Registry<RibbonGroup>('ribbonGroups'),
  ribbonItems: new Registry<RibbonItem>('ribbonItems'),
  contextMenu: new Registry<ContextMenuProvider>('contextMenu'),
  paste: new Registry<PasteHandler & { order?: number }>('paste'),
  htmlTransforms: new Registry<HtmlTransform>('htmlTransforms'),
  extensions: new Registry<ExtensionSpec>('extensions'),
  nodeViews: new Registry<NodeViewSpec>('nodeViews'),
  panels: new Registry<PanelSpec>('panels'),
  lint: new Registry<LintRule>('lint'),
  dialogs: new Registry<DialogSpec>('dialogs'),
  statusbar: new Registry<StatusItem>('statusbar'),
  startSections: new Registry<StartSection>('startSections'),
  sheets: new Registry<SheetSpec>('sheets'),
  events: new Events(),
};
export type RegistryApi = typeof registry;

/** Функция подключения возможности: src/features/<имя>/index.ts экспортирует `export default function setup(r)`. */
export type FeatureSetup = (r: RegistryApi) => void;
